import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Button, Card, Grid, Group, Loader, NumberInput, Select, Stack, Switch, Text, TextInput, Title } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useLiveCollection, useLiveDoc } from "./useLive";
import { CtcCommission, CtcCurrency, CtcCustomer, CtcDynamicFieldDef, CtcPurchaseOrder } from "../../types";
import { ctcValueDay, useCtcCallables, useCtcUser } from "./ctc_helpers";
import { isCtcActive } from "../../../../shared/ctcMasterData";
import { CtcAction } from "../../../../shared/ctcLifecycle";

const DIAL_CODES = ["+965", "+973", "+966", "+971", "+974", "+968", "+20", "+44", "+1"];

/**
 * Maker form for a new or draft purchase order. Mirrors the legacy "addPO" screen:
 * amount, currency, commission profile (trading type), beneficiary number + WhatsApp mobile,
 * client reference, value date (treasury), and the customer's dynamic fields.
 * Submission is a separate lifecycle action on the detail page.
 */
const CtcNewOrder = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const user = useCtcUser();
    const { saveOrder, transition } = useCtcCallables();
    const [saving, setSaving] = useState<false | "draft" | "submit">(false);
    const submitAfterSave = useRef(false); // which button sent the form: Save draft or Submit

    const customers = useLiveCollection<CtcCustomer>("ctc_customers").data;
    const currencies = useLiveCollection<CtcCurrency>("ctc_currencies").data;
    const commissions = useLiveCollection<CtcCommission>("ctc_commissions").data;
    const { data: existing, loading: loadingExisting } = useLiveDoc<CtcPurchaseOrder>(id ? `ctc_purchase_orders/${id}` : null);
    const draft = id ? existing : undefined;

    const form = useForm({
        initialValues: {
            customerId: user.customerId ?? "",
            clientRefNo: "",
            amount: 0 as number | string,
            currencyId: "",
            commissionId: "",
            valueDate: null as string | null, // Mantine 9 DateInput value: "YYYY-MM-DD"
            beneficiaryNumber: "",
            mobileCode: "+965",
            mobile: "",
            email: "",
            language: "ar" as "ar" | "en",
            dynamicFields: {} as Record<string, string | number | boolean>,
        },
        validate: {
            customerId: (v) => (v ? null : "Customer is required"),
            amount: (v) => (Number(v) > 0 ? null : "Amount must be positive"),
            currencyId: (v) => (v ? null : "Required"),
            commissionId: (v) => (v ? null : "Required"),
            beneficiaryNumber: (v) => (v.trim() ? null : "Required"),
            mobileCode: (v) => (/^\+\d{1,4}$/.test(v) ? null : "+ and 1-4 digits"),
            mobile: (v) => (/^\d{6,15}$/.test(v) ? null : "6-15 digits, no dial code"),
            // Required: the beneficiary's questions also go by email (the server checks the same)
            email: (v) => (!v.trim() ? "Required" : /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.trim()) ? null : "Invalid email"),
        },
    });

    useEffect(() => {
        if (!draft) return;
        form.setValues({
            customerId: draft.customerId,
            clientRefNo: draft.clientRefNo ?? "",
            amount: draft.amount,
            currencyId: draft.currencyId,
            commissionId: draft.commissionId,
            valueDate: ctcValueDay(draft.valueDate) ?? null,
            beneficiaryNumber: draft.beneficiary?.number ?? "",
            mobileCode: draft.beneficiary?.mobile_code ?? "+965",
            mobile: draft.beneficiary?.mobile ?? "",
            email: draft.beneficiary?.email ?? "",
            language: draft.beneficiary?.language ?? "ar",
            dynamicFields: draft.dynamicFields ?? {},
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draft?.id]);

    // Makers create orders only for customers whose Microsoft group they're in (server enforces the same)
    // The customers the user's Microsoft groups link them to decide who they create orders for;
    // only an admin outside every customer group may pick any customer
    const activeCustomers = (customers ?? []).filter(isCtcActive);
    const groupCustomers = activeCustomers.filter((c) => user.groupCustomerIds.includes(c.id!));
    const allowedCustomers = groupCustomers.length ? groupCustomers : user.isAdmin ? activeCustomers : [];
    const canCreateFor = (customerId: string) => allowedCustomers.some((c) => c.id === customerId);
    // New order: pick the customer automatically when the user's groups allow exactly one
    useEffect(() => {
        if (id || form.values.customerId || allowedCustomers.length !== 1) return;
        form.setFieldValue("customerId", allowedCustomers[0].id!);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, allowedCustomers.length, allowedCustomers[0]?.id]);
    const noCustomer = !!customers && !id && (allowedCustomers.length === 0 || (!!form.values.customerId && !canCreateFor(form.values.customerId)));
    // One possible customer (or editing a draft, which can't change customer): show it, no dropdown
    const customerIsFixed = !!id || allowedCustomers.length === 1;

    const customer = customers?.find((c) => c.id === form.values.customerId);
    const dynamicFields = useLiveCollection<CtcDynamicFieldDef>(form.values.customerId ? `ctc_customers/${form.values.customerId}/dynamic_fields` : null).data;
    const activeFields = [...(dynamicFields ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const setDynamic = (fieldId: string, value: string | number | boolean | undefined) => {
        const next = { ...form.values.dynamicFields };
        if (value === undefined || value === "") delete next[fieldId]; else next[fieldId] = value;
        form.setFieldValue("dynamicFields", next);
    };

    // "Allow orders only in this currency": the currency is the customer's and can't be changed (server enforces it)
    const lockedCurrency = customer?.isRestrictedCurrency && customer.currencyId ? customer.currencyId : undefined;
    useEffect(() => {
        if (lockedCurrency && form.values.currencyId !== lockedCurrency) form.setFieldValue("currencyId", lockedCurrency);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lockedCurrency, form.values.currencyId]);
    // New order: start with a currency chosen (the customer's own, else the only active one); still changeable
    useEffect(() => {
        if (id || lockedCurrency || form.values.currencyId || !currencies) return;
        const active = currencies.filter(isCtcActive);
        const pick = active.find((c) => c.id === customer?.currencyId) ?? (active.length === 1 ? active[0] : undefined);
        if (pick) form.setFieldValue("currencyId", pick.id!);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, lockedCurrency, form.values.currencyId, customer?.currencyId, currencies]);
    // Limits shown under the amount; the server checks them on save (converting other currencies)
    const limitCurrency = customer?.currencyId ?? "";
    const limitHints = [
        customer?.amountLimit?.active && customer.amountLimit.amount > 0 ? `Max ${customer.amountLimit.amount.toLocaleString("en-US")} ${customer.amountLimit.currencyId || limitCurrency} per order` : undefined,
        customer?.beneficiaryLimit?.active && customer.beneficiaryLimit.amount > 0 ? `max ${customer.beneficiaryLimit.amount.toLocaleString("en-US")} ${limitCurrency} of open orders per beneficiary` : undefined,
    ].filter(Boolean).join(" · ");
    // Live check of the per-order limit when the order is in the limit's currency (other currencies are
    // converted and checked by the server on save; the beneficiary total needs the open orders: server too)
    const limitAmount = customer?.amountLimit?.active && customer.amountLimit.amount > 0 ? customer.amountLimit.amount : undefined;
    const limitAmountCurrency = customer?.amountLimit?.currencyId || limitCurrency;
    const amountOverLimit = limitAmount !== undefined && form.values.currencyId === limitAmountCurrency && Number(form.values.amount) > limitAmount ?
        `Above this customer's limit of ${limitAmount.toLocaleString("en-US")} ${limitAmountCurrency} per order` : undefined;

    // Only the commission profiles linked to the customer (server enforces the same). A draft's
    // current profile stays visible, labelled, if it was retired since.
    const linkedCommissions = (commissions ?? []).filter((c) => !!customer?.commissionIds?.includes(c.id!) && (isCtcActive(c) || c.id === form.values.commissionId));
    const activeLinkedCommissions = linkedCommissions.filter(isCtcActive);
    useEffect(() => {
        if (!customer || !commissions) return;
        const current = form.values.commissionId;
        // A profile the customer isn't linked to can't be kept (e.g. after changing customer)
        if (current && !customer.commissionIds?.includes(current)) {
            form.setFieldValue("commissionId", "");
            return;
        }
        // Only one linked profile: pick it
        if (!current && activeLinkedCommissions.length === 1) form.setFieldValue("commissionId", activeLinkedCommissions[0].id!);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customer?.id, customer?.commissionIds?.join(","), commissions?.length, activeLinkedCommissions.length]);
    const noCommission = !!customer && !!commissions && activeLinkedCommissions.length === 0;

    if (id && loadingExisting) return <Loader />;
    if (id && draft && draft.tags?.poStatus?.code !== "draft") {
        return <Text c="red">Only drafts can be edited. <Button variant="subtle" onClick={() => navigate(`/ctc/orders/${id}`)}>Back to order</Button></Text>;
    }

    // Any error shown on the form (field checks after a save attempt, or the live limit check) blocks saving
    const hasErrors = !!amountOverLimit || Object.keys(form.errors).length > 0;
    const submit = form.onSubmit(async (values) => {
        if (amountOverLimit) return; // shown under Amount
        // Required dynamic fields (Yes / No is always answered); the server checks the same
        const missing = activeFields.filter((f) => f.required && f.type !== "bool" && `${values.dynamicFields[f.id!] ?? ""}`.trim() === "");
        if (missing.length) {
            notifications.show({ color: "red", message: `Fill in: ${missing.map((f) => f.name_en).join(", ")}` });
            return;
        }
        const andSubmit = submitAfterSave.current;
        submitAfterSave.current = false; // Enter in a field afterwards means Save draft again
        if (andSubmit && !window.confirm("Save and submit this order?\n\nIt can't be edited after it is submitted.")) return;
        setSaving(andSubmit ? "submit" : "draft");
        try {
            const result = await saveOrder({
                poId: id,
                customerId: values.customerId,
                clientRefNo: values.clientRefNo,
                amount: Number(values.amount),
                currencyId: values.currencyId,
                commissionId: values.commissionId,
                valueDate: values.valueDate || undefined, // "YYYY-MM-DD", stored as that text
                beneficiary: {
                    number: values.beneficiaryNumber,
                    mobile: values.mobile,
                    mobile_code: values.mobileCode,
                    email: values.email,
                    language: values.language,
                },
                dynamicFields: values.dynamicFields,
            });
            if (andSubmit) {
                // Saved first, so a failed submit still leaves the draft (opened below, to fix and submit there)
                try {
                    await transition({ poId: result.data.poId, action: CtcAction.submit });
                    notifications.show({ color: "green", message: `Submitted ${result.data.referenceNumber}` });
                } catch (e) {
                    notifications.show({ color: "orange", title: `Saved as draft ${result.data.referenceNumber}, not submitted`, message: (e as Error).message || "Submit failed" });
                }
            } else {
                notifications.show({ color: "green", message: `Saved draft ${result.data.referenceNumber}` });
            }
            navigate(`/ctc/orders/${result.data.poId}`);
        } catch (e) {
            console.error("ctcSaveOrder failed", e);
            notifications.show({ color: "red", message: (e as Error).message || "Save failed" });
        } finally {
            setSaving(false);
        }
    });

    // Only makers (and admins) create or edit orders; the server refuses everyone else too
    if (!user.has("maker") && !user.isAdmin) {
        return <Alert color="orange" title="Not allowed">Only makers can create or edit purchase orders.</Alert>;
    }

    return (
        <form onSubmit={submit}>
            <Group justify="space-between" mb="md">
                <Title order={3}>{id ? `Edit draft ${draft?.referenceNumber ?? ""}` : "New purchase order"}</Title>
                <Group>
                    <Button variant="default" onClick={() => navigate(id ? `/ctc/orders/${id}` : "/ctc/orders")}>Cancel</Button>
                    <Button type="submit" variant="light" loading={saving === "draft"} disabled={!!saving || noCustomer || noCommission || hasErrors} onClick={() => { submitAfterSave.current = false; }}>Save draft</Button>
                    <Button type="submit" loading={saving === "submit"} disabled={!!saving || noCustomer || noCommission || hasErrors} onClick={() => { submitAfterSave.current = true; }}>Submit</Button>
                </Group>
            </Group>
            {noCustomer && (
                <Alert color="orange" mb="md" title="You can't create orders for any customer">
                    Orders can only be created by members of the customer's Microsoft group. Ask back office to set the customer's
                    Microsoft group ID, and IT to add you to that group, then refresh the page.
                </Alert>
            )}
            <Grid>
                <Grid.Col span={{ md: 6, xs: 12 }}>
                    <Card withBorder>
                        <Text fw={700} mb="sm">Order</Text>
                        <Stack>
                            {!noCustomer && (customerIsFixed ? (
                                <TextInput
                                    label="Customer"
                                    value={customers?.find((c) => c.id === form.values.customerId)?.name_en ?? ""}
                                    readOnly
                                    description={id ? "A draft can't change customer" : groupCustomers.length ? "From your Microsoft group" : undefined}
                                />
                            ) : (
                                <Select
                                    label="Customer"
                                    required
                                    data={allowedCustomers.map((c) => ({ value: c.id!, label: c.name_en }))}
                                    description={groupCustomers.length ? "Customers from your Microsoft groups" : undefined}
                                    {...form.getInputProps("customerId")}
                                    // Another customer has other dynamic fields: start them empty
                                    onChange={(v) => {
                                        if (v === form.values.customerId) return;
                                        form.setFieldValue("customerId", v ?? "");
                                        form.setFieldValue("dynamicFields", {});
                                    }}
                                />
                            ))}
                            <NumberInput label="Amount" required min={0} decimalScale={3} thousandSeparator="," description={limitHints || undefined} {...form.getInputProps("amount")} error={form.errors.amount ?? amountOverLimit} />
                            {/* Active only; a draft's current value stays visible (labelled) and the server rejects it on save */}
                            <Select label="Currency" required readOnly={!!lockedCurrency} description={lockedCurrency ? `This customer only accepts orders in ${lockedCurrency}` : undefined} data={(currencies ?? []).filter((c) => isCtcActive(c) || c.id === form.values.currencyId).map((c) => ({ value: c.id!, label: `${c.shortName} — ${c.name_en}${isCtcActive(c) ? "" : " (no longer available)"}` }))} {...form.getInputProps("currencyId")} />
                            <Select
                                label="Commission profile (trading type)"
                                required
                                data={linkedCommissions.map((c) => ({ value: c.id!, label: `${c.name_en}${isCtcActive(c) ? "" : " (no longer available)"}` }))}
                                readOnly={activeLinkedCommissions.length === 1 && form.values.commissionId === activeLinkedCommissions[0].id}
                                description={noCommission ? undefined : "Profiles linked to this customer"}
                                {...form.getInputProps("commissionId")}
                                error={noCommission ? "No commission profile is linked to this customer. Ask back office to link one (Back office → Customers → Commission profiles)." : form.getInputProps("commissionId").error}
                            />
                            <TextInput label="Client reference" {...form.getInputProps("clientRefNo")} />
                            {customer?.isTreasury && <DateInput label="Value date" clearable {...form.getInputProps("valueDate")} />}
                            {/* The customer's own fields (Back office → Customers → Dynamic fields) */}
                            {activeFields.map((f) => {
                                const value = form.values.dynamicFields[f.id!];
                                const label = f.name_en;
                                if (f.type === "bool") {
                                    return <Switch key={f.id} label={label} checked={value === true} onChange={(e) => setDynamic(f.id!, e.currentTarget.checked)} />;
                                }
                                if (f.type === "number") {
                                    return <NumberInput key={f.id} label={label} required={f.required} thousandSeparator="," value={typeof value === "number" ? value : value === undefined ? "" : Number(value)}
                                        onChange={(v) => setDynamic(f.id!, v === "" ? undefined : Number(v))} />;
                                }
                                return <TextInput key={f.id} label={label} required={f.required} value={value === undefined ? "" : String(value)} onChange={(e) => setDynamic(f.id!, e.currentTarget.value)} />;
                            })}
                        </Stack>
                    </Card>
                </Grid.Col>
                <Grid.Col span={{ md: 6, xs: 12 }}>
                    <Card withBorder>
                        <Text fw={700} mb="sm">Beneficiary</Text>
                        <Stack>
                            <TextInput label="Beneficiary number" required description="The bank's reference for the end client" {...form.getInputProps("beneficiaryNumber")} />
                            <Group grow align="flex-start">
                                <Select label="Dial code" required data={DIAL_CODES} searchable {...form.getInputProps("mobileCode")} />
                                <TextInput label="WhatsApp number" required description="Digits only, without the country code" inputMode="numeric" maxLength={15}
                                    {...form.getInputProps("mobile")} onChange={(e) => form.setFieldValue("mobile", e.currentTarget.value.replace(/\D/g, "").slice(0, 15))} />
                            </Group>
                            <Select label="Attachment, WhatsApp and email language (Default Arabic)" data={[{ value: "ar", label: "العربية" }, { value: "en", label: "English" }]} {...form.getInputProps("language")} />
                            <TextInput label="Email" required description="Approval questions are sent here as well as on WhatsApp" {...form.getInputProps("email")} />
                            <Text size="xs" c="dimmed">Approval requests and the choice to sell or collect are sent to this WhatsApp number and email. The first answer counts.</Text>
                        </Stack>
                    </Card>
                </Grid.Col>
            </Grid>
        </form>
    );
};

export default CtcNewOrder;
